import { useEffect, useRef, useState } from "preact/hooks";

type Upload = {
  id: string;
  url: string;
  contentType: string;
  sizeBytes: number;
  createdAt: number;
};

type Stock = { url: string; label: string };

const STOCK: Stock[] = [
  { url: "https://picsum.photos/seed/stock-1/600/400", label: "Stock 1" },
  { url: "https://picsum.photos/seed/stock-2/600/400", label: "Stock 2" },
  { url: "https://picsum.photos/seed/stock-3/600/400", label: "Stock 3" },
  { url: "https://picsum.photos/seed/stock-4/600/400", label: "Stock 4" },
  { url: "https://placehold.co/600x400/0b0d12/ffffff?text=Placeholder", label: "Dark placeholder" },
  { url: "https://placehold.co/600x400/ffffff/0b0d12?text=Placeholder", label: "Light placeholder" },
];

export function AssetPicker({
  apiBase,
  token,
  canUpload,
  onPick,
  onClose,
}: {
  apiBase: string;
  token: string;
  canUpload: boolean;
  onPick: (url: string) => void;
  onClose: () => void;
}) {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${apiBase}/api/uploads`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = (await res.json()) as { uploads: Upload[] };
          setUploads(data.uploads ?? []);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase, token]);

  async function onFileChange(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    input.value = "";
    setError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${apiBase}/api/uploads`, {
        method: "POST",
        headers: { authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(uploadErrorLabel(data.error));
        return;
      }
      setUploads((list) => [data.upload, ...list]);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div class="asset-picker">
      <div class="asset-picker-head">
        <div class="asset-picker-title">Pick an image</div>
        <button class="icon-btn" onClick={onClose} aria-label="Close">×</button>
      </div>
      {canUpload ? (
        <div class="asset-picker-actions">
          <button
            class="btn primary"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? "Uploading…" : "Upload image"}
          </button>
          <span class="muted" style={{ fontSize: "11px" }}>
            PNG / JPG / WEBP / GIF · max 10MB
          </span>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            style={{ display: "none" }}
            onChange={onFileChange}
          />
        </div>
      ) : (
        <div class="asset-picker-actions muted" style={{ fontSize: "11px" }}>
          Uploads are disabled on the public demo. Pick from the stock palette below.
        </div>
      )}
      {error && <div class="asset-picker-error">{error}</div>}

      {canUpload && (
        <div class="asset-picker-section">
          <div class="asset-picker-section-label">Your uploads</div>
          {loading ? (
            <div class="muted" style={{ fontSize: "11px" }}>Loading…</div>
          ) : uploads.length === 0 ? (
            <div class="muted" style={{ fontSize: "11px" }}>No uploads yet.</div>
          ) : (
            <div class="asset-grid">
              {uploads.map((u) => (
                <button
                  key={u.id}
                  class="asset-tile"
                  onClick={() => onPick(u.url)}
                  title={new Date(u.createdAt).toLocaleString()}
                >
                  <img src={u.url} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div class="asset-picker-section">
        <div class="asset-picker-section-label">Stock</div>
        <div class="asset-grid">
          {STOCK.map((s) => (
            <button
              key={s.url}
              class="asset-tile"
              onClick={() => onPick(s.url)}
              title={s.label}
            >
              <img src={s.url} alt={s.label} loading="lazy" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function uploadErrorLabel(code: unknown): string {
  switch (code) {
    case "file_too_large":
      return "File too large — 10 MB max.";
    case "unsupported_image_format":
      return "Only PNG, JPEG, WEBP, or GIF are allowed.";
    case "storage_quota_exceeded":
      return "Project storage quota reached.";
    case "uploads_disabled_for_demo":
      return "Uploads aren't available on the demo project.";
    case "rate_limited":
      return "Too many uploads. Try again later.";
    default:
      return "Upload failed. Try again.";
  }
}
